import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Table } from 'antd';
import BatchUploader from '../../common/BatchUploader';
import { ErrorText } from '../../components/text/ErrorText';
import { CONFIG } from '../../config/Config';
import { useGetAlgorithm } from '../../lib/hooks';
import { useInterval } from '../../lib/useInterval';
import { anyRecentProcessingInference } from '../../lib/util/processing';
import { Inference, InferenceFile } from '../../types';
import { PublicDemoHeader } from './PublicDemoHeader';
import { GetPublicDemoTableColumns } from './PublicDemoTableColumns';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { useParams } from 'react-router';
import HeroImage from '../../images/ixordocs-hero.png';
import { getInferencePublic } from '../../lib/api/public-api';

const PublicDemo = () => {
  const { t, i18n } = useTranslation();
  const algorithmId = '9';

  const { ln } = useParams() as {
    ln: string;
  };

  useEffect(() => {
    if (ln) {
      i18n.changeLanguage(`${ln}-BE`);
    }
  }, [ln, i18n]);

  const { algorithm } = useGetAlgorithm(algorithmId, true);

  //list of id's in localstorage:
  const [ids, setIds] = useState<string[]>(
    localStorage.getItem('ids') ? JSON.parse(localStorage.getItem('ids') || '{}') : []
  );
  //actual inferences:
  const [inferences, setInferences] = useState<Inference[] | null>(null);

  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchInferences = useCallback(
    async (load: boolean) => {
      if (load) setLoading(true);
      if (ids.length) {
        const results = await Promise.all(
          ids.map(async id => {
            let inf;
            try {
              inf = await getInferencePublic(id);
            } catch (e) {
              console.log(e);
              inf = 0;
            }
            return inf;
          })
        );
        const inferences: Inference[] = results.filter(i => i !== 0) as Inference[];
        if (inferences?.length === 0) {
          setError(true);
        } else {
          setError(false);
          setInferences(inferences);
        }
      } else {
        setError(false);
        setInferences([]);
      }
      if (load) setLoading(false);
    },
    [ids]
  );

  useEffect(() => {
    fetchInferences(true);
  }, [fetchInferences, ids]);

  useEffect(() => {
    localStorage.setItem('ids', JSON.stringify(ids));
  }, [ids]);

  useInterval(async () => {
    if (!algorithm) return;

    try {
      if (anyRecentProcessingInference(inferences || [])) {
        fetchInferences(false);
      }
    } catch (e) {}
  }, 2000);

  const onClickUrl = useCallback((record: InferenceFile) => {
    return `/document-recognition/detail/${record.id}`;
  }, []);

  const columns = GetPublicDemoTableColumns(onClickUrl);

  return (
    <div className="h-full w-full public-demo flex flex-col">
      <PublicDemoHeader />
      <div
        className=""
        style={{
          backgroundImage: 'linear-gradient(90deg, #005ea0 19%, #00aeef 83%, #00b9ff)',
        }}>
        <div className="w-4/5 mx-auto py-6 md:py-14 flex flex-col md:flex-row items-center">
          <div className="w-full md:w-1/2">
            <div className="mb-6">
              <h1 className="text-xl md:text-4xl text-white font-bold mb-0">{t('public.overview.title')}</h1>
            </div>
            <div className="mb-6">
              <p className="text-base text-white leading-6">{t('public.overview.intro')}</p>
            </div>
            <div>
              <a
                href="https://calendly.com/ward-vanlaer"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cta inline-block white py-3 px-6 rounded-2xl text-lg font-medium leading-4 "
                style={{ border: '1px solid #ff980b' }}>
                {t('public.overview.book-demo')}
              </a>
            </div>
          </div>
          <div className="w-full md:w-1/2 flex justify-center md:justify-end mt-8 md:mt-0">
            <img src={HeroImage} alt="IxorDocs" className="w-32 md:w-60 " />
          </div>
        </div>
      </div>

      <div className="bg-white flex-grow w-full py-8">
        <div className="w-4/5 mx-auto">
          {isMobile ? (
            <div className="mb-8">
              <Alert message={t('public.overview.mobile-warning')} type="warning" />
            </div>
          ) : null}
          <div className="mb-8">
            {ids && ids?.length >= 8 ? (
              <div className="w-full flex flex-col items-center justify-center">
                <Alert message={t('public.overview.max-docs')} type="warning" />
              </div>
            ) : (
              <BatchUploader
                multiple={true}
                isMultiFlow={true}
                modelId={10000000}
                modelVersion={null}
                getPresignedParams={{ customer: CONFIG.multiflow.customer, flowName: 'invoice' }}
                getPresignedUrl={'/api/generate-presigned-urls'}
                onBatchUploaded={(inferenceIds: any[]) => {
                  setIds(prev => [...prev, ...inferenceIds]);
                }}
                publicDemo={true}
                ids={ids}
              />
            )}
          </div>
          {error ? (
            <ErrorText>Something went wrong. We could not find the documents you were looking for.</ErrorText>
          ) : inferences?.length ? (
            <>
              <Table
                bordered
                className="ant-table-toolbox"
                columns={columns}
                rowKey={record => record.id}
                dataSource={inferences || []}
                loading={loading}
                pagination={false}
              />
              <div className="flex justify-end my-4">
                <div className="mr-4">
                  {inferences?.length} {t('public.table.documents')}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PublicDemo;
